import React from 'react';
import { ClockIcon, MapPinIcon } from 'lucide-react';
import { doodleCatalog } from '../../data/doodleShapes';
import type { WelcomeWeekDay } from '../../data/welcomeWeek';

/**
 * One night of Welcome Week. Each day gets its own accent band and corner shape
 * so the five cards feel like a set rather than five identical boxes.
 *
 * A pending location is shown as a deliberate dashed chip — never hidden, and
 * never filled in with a guessed room.
 */

const accents = [{
  band: 'bg-brand-blue',
  shape: doodleCatalog.navy.stars[3]
}, {
  band: 'bg-brand-yellow',
  shape: doodleCatalog.navy.organics[2]
}, {
  band: 'bg-brand-tan',
  shape: doodleCatalog.navy.stars[8]
}, {
  band: 'bg-brand-navy',
  shape: doodleCatalog.yellow.stars[5]
}, {
  band: 'bg-brand-pink',
  shape: doodleCatalog.navy.organics[7]
}];
export function WelcomeWeekCard({
  event,
  index,
  featured = false
}: {
  event: WelcomeWeekDay;
  index: number;
  featured?: boolean;
}) {
  const accent = accents[index % accents.length];
  const onDarkBand = accent.band === 'bg-brand-navy';
  return <article className="flex h-full flex-col overflow-hidden rounded-[20px] border-2 border-brand-navy bg-white transition-transform duration-200 hover:-translate-y-1" data-id="element-383" data-mp="fc158s9dkz5u">
      <div className={`relative flex items-baseline justify-between gap-3 overflow-hidden px-5 py-3 ${accent.band}`} data-id="element-384" data-mp="6e7ia7xi3hvc">
        <img src={accent.shape} alt="" aria-hidden="true" draggable={false} className="pointer-events-none absolute -right-2 -top-2 h-10 w-10 rotate-12 object-contain opacity-70" data-id="element-385" data-mp="z2d5r2fxvkyv" />
        <p className={`font-display text-lg font-extrabold leading-none ${onDarkBand ? 'text-white' : 'text-brand-navy'}`} data-id="element-386" data-mp="pbi7t0tbywxl">
          {event.day}
        </p>
        <p className={`relative font-meta text-[13px] font-semibold ${onDarkBand ? 'text-white' : 'text-brand-navy'}`} data-id="element-387" data-mp="1htw6t3qekoy9">
          {event.date}
        </p>
      </div>

      <div className={`flex flex-1 flex-col p-5 ${featured ? 'sm:p-6' : ''}`} data-id="element-388" data-mp="37b9ixielsurk">
        <h3 className={`font-display font-bold leading-snug text-brand-navy ${featured ? 'text-xl sm:text-2xl' : 'text-lg'}`} data-id="element-389" data-mp="3bbti0riqm8gc">
          {event.title}
        </h3>

        <dl className="mt-3 space-y-2" data-id="element-390" data-mp="32i83n9ahit7g">
          <div className="flex items-start gap-2" data-id="element-391" data-mp="32h5bky1fekpy">
            <dt className="sr-only" data-id="element-392" data-mp="2nkxtq4dhpvdu">Time</dt>
            <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-393" data-mp="2no3kdo1rp5bo" />
            <dd className="font-meta text-[14px] font-semibold text-brand-navy" data-id="element-394" data-mp="2sizaujrcvkmo">{event.time}</dd>
          </div>

          <div className="flex items-start gap-2" data-id="element-395" data-mp="2enniuk4l489g">
            <dt className="sr-only" data-id="element-396" data-mp="2jl6vjflmwcza">Location</dt>
            <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-397" data-mp="ctxzvszzca5m" />
            <dd className="font-sans text-[14px] leading-snug text-ink" data-id="element-398" data-mp="cs3aa1drjtv7">
              {event.location ? event.location : <span className="inline-flex rounded-full border border-dashed border-brand-navy px-2.5 py-1 font-meta text-[12.5px] font-semibold text-brand-navy" data-id="element-399" data-mp="rxh8p8g60711">
                  {event.locationPending}
                </span>}
            </dd>
          </div>
        </dl>

        {event.callout ? <p className="mt-3 inline-flex w-fit rounded-full bg-brand-yellow px-3 py-1 font-meta text-[12.5px] font-semibold text-[#3d2b00]" data-id="element-400" data-mp="vx9h9l5k6d6e">
            {event.callout}
          </p> : null}

        <p className="mt-3 font-sans text-[14.5px] leading-relaxed text-ink" data-id="element-401" data-mp="15qgn1747nv70">{event.description}</p>
      </div>
    </article>;
}